import type { StanDataColumn } from '@/components/core-components/StanDataRow/StanDataRow.types';
import type { FlowSummary } from '@/lib/api/email-flows/types';
import type { IconComponent } from '@/components/core-components/types';
import { StanBadge } from '@/components/core-components/StanBadge/StanBadge';

export interface FlowAnalyticsData {
    sales: string;
    sent: number;
    openRate: string;
    clickRate: string;
}

export function buildFlowColumns(
    flow: FlowSummary,
    FlowIcon: IconComponent,
    analyticsData: FlowAnalyticsData,
    flowStatus: 'active' | 'draft',
    isMobile: boolean
): StanDataColumn[] {
    // Custom mobile view - matches vue implementation
    if (isMobile) {
        return [
            {
                id: 'name',
                name: 'Flow',
                value: (
                    <div className='flex min-w-0 flex-1 flex-col gap-3'>
                        <div className='flex gap-1'>
                            <FlowIcon className='size-5 shrink-0 -translate-y-0.5' />
                            <p className='text-label-sm-bold leading-tight'>
                                {flow.data?.name || `Flow ${flow.flow_id}`}
                            </p>
                        </div>
                        <div className='flex items-center gap-1.5'>
                            <span className='text-label-xs-regular'>Status:</span>
                            <StanBadge variant={flowStatus === 'active' ? 'success' : 'muted'} size='sm'>
                                {flowStatus === 'active' ? 'Active' : 'Draft'}
                            </StanBadge>
                        </div>
                        <div className='flex flex-wrap items-center gap-x-4 gap-y-2'>
                            <div className='flex items-center gap-1.5'>
                                <span className='text-label-xs-regular'>Sales:</span>
                                <span className='text-label-sm-bold'>{analyticsData.sales}</span>
                            </div>
                            <div className='flex items-center gap-1.5'>
                                <span className='text-label-xs-regular'>Sent:</span>
                                <span className='text-label-sm-bold'>{analyticsData.sent.toLocaleString()}</span>
                            </div>
                            <div className='flex items-center gap-1.5'>
                                <span className='text-label-xs-regular'>Open Rate:</span>
                                <span className='text-label-sm-bold'>{analyticsData.openRate}</span>
                            </div>
                            <div className='flex items-center gap-1.5'>
                                <span className='text-label-xs-regular'>Click Rate:</span>
                                <span className='text-label-sm-bold'>{analyticsData.clickRate}</span>
                            </div>
                        </div>
                    </div>
                ),
            },
        ];
    }

    // Desktop view - show all columns separately
    return [
        {
            id: 'name',
            name: 'Flow',
            value: (
                <p className='text-label-md-bold xl:text-label-lg-bold md:max-w-full md:truncate'>
                    {flow.data?.name || `Flow ${flow.flow_id}`}
                </p>
            ),
            icon: FlowIcon,
        },
        {
            id: 'sales',
            name: 'Sales',
            value: <span className='font-semibold'>{analyticsData.sales}</span>,
        },
        {
            id: 'sent',
            name: 'Sent',
            value: <span className='font-semibold'>{analyticsData.sent.toLocaleString()}</span>,
        },
        {
            id: 'open-rate',
            name: 'Open Rate',
            value: <span className='font-semibold'>{analyticsData.openRate}</span>,
        },
        {
            id: 'click-rate',
            name: 'Click Rate',
            value: <span className='font-semibold'>{analyticsData.clickRate}</span>,
        },
        {
            id: 'status',
            name: 'Status',
            type: 'pill',
            value: flowStatus === 'active' ? 'Active' : 'Draft',
            statusVariant: flowStatus,
        },
    ];
}
